import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { formatDate, FORMNAMES, setBrowserTitle } from "../utilities/codes";
import { FaInbox } from "react-icons/fa";
import Breadcrumb from "../components/Breadcrumb";
import { getFormEntriesByFormName } from "../services/formServices";
import toast from "react-hot-toast";
import { I18N_KEYS } from "../languages/PlaceHolder";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const paperColors = [
    "bg-yellow-100",
    "bg-pink-100",
    "bg-green-100",
    "bg-blue-100",
    "bg-orange-100",
    "bg-red-100",
];

const pins = [
    "bg-red-500",
    "bg-blue-500",
    "bg-green-500",
    "bg-yellow-500",
    "bg-pink-500",
];

const cardVariants = {
    hidden: {
        opacity: 0,
        y: 40,
        scale: 0.95,
    },
    show: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.5, ease: "easeOut" }
    },
};
const NoticeBoard = () => {
    const [selectedNotice, setSelectedNotice] = useState(null);
    const [notices, setNotices] = useState([]);
    const { t } = useTranslation()
    const randomRotations = useMemo(() => {
        return notices.map(() => Math.floor(Math.random() * 10) - 5);
    }, [notices]);

    useEffect(() => {
        setBrowserTitle("Notice");
        getNotices()
    }, []);

    const getNotices = async () => {
        try {
            const response = await getFormEntriesByFormName(FORMNAMES.NOTICE);
            if (response && response.length > 0) {
                setNotices(response)
            }
        } catch (error) {
            toast.error(error.message);
        }
    };

    return (
        <>
            <Breadcrumb />

            {notices.length > 0 ? <div className="min-h-[750px] p-6 rounded-3xl shadow-2xl bg-gradient-to-br from-gray to-white-100">
                <motion.h2
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-center gap-3 text-4xl font-bold text-center text-primary mb-20"
                >
                    {t(I18N_KEYS.NOTICEBOARDNAME)}
                </motion.h2>
                <motion.div
                    initial="hidden"
                    animate="show"
                    className="sm:columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-12 text-center common-container"
                >
                    {notices?.map((notice, index) => (
                        <motion.div
                            layout
                            key={index}
                            variants={cardVariants}
                            style={{ rotate: randomRotations[index] }}
                            onClick={() => setSelectedNotice(notice)}
                            initial={{ rotate: randomRotations[index], opacity: 0 }}
                            animate={{ rotate: randomRotations[index], opacity: 1 }}
                            whileHover={{
                                scale: 1.05,
                                rotate: 0,
                                y: -8,
                            }}
                            className={`cursor-pointer relative inline-block w-full mb-12 break-inside-avoid p-4 rounded-xl shadow-lg ${paperColors[index % paperColors.length]}`}
                        >
                            <motion.div
                                className={`absolute -top-3 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full shadow-md ${pins[index % pins.length]}`}
                            />
                            {notice?.payload?.title &&
                                <h3 className="font-bold text-lg mb-2 text-center text-primary capitalize">
                                    {notice.payload?.title}
                                </h3>}

                            {notice?.payload?.date &&
                                <p className="text-sm leading-relaxed text-center text-primary">
                                    {formatDate(notice.payload?.date)}
                                </p>}

                            {notice?.payload?.message &&
                                <p className="text-center text-sm text-primary mb-4">
                                    {notice.payload?.message}
                                </p>
                            }
                        </motion.div>
                    ))}
                </motion.div>
            </div> :
                <section className="p-32 flex items-center justify-center from-slate-50 via-blue-50 to-indigo-50 px-6">
                    <div className="flex flex-col items-center justify-center space-y-4">
                        <div className="flex items-center justify-center w-24 h-24 rounded-full bg-blue-100 text-blue-600 shadow-md animate-bounce mb-4">
                            <FaInbox className="text-5xl text-primary" />
                        </div>
                        <h2 className="text-2xl md:text-3xl font-semibold text-primary">
                            {t(I18N_KEYS.NOTICETITLE)}
                        </h2>
                        <p className="text-gray-700 text-center text-base md:text-lg max-w-2xl mx-auto">
                            {t(I18N_KEYS.NOTICEDES)}
                        </p>
                        <Link
                            to="/"
                            className="bg-primary text-accent px-8 py-4 rounded-full text-lg font-semibold hover:bg-primary/90 transition-colors shadow-lg hover:shadow-xl transform hover:scale-105 transition-transform"
                        >
                            {t(I18N_KEYS.GOTO)} {t(I18N_KEYS.HOMEPAGENAME)}
                        </Link>
                    </div>
                </section>
            }

            {selectedNotice && (
                <AnimatePresence>
                    <motion.div
                        className="fixed inset-0 z-50 bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-sm flex items-center justify-center p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSelectedNotice(null)}
                    >
                        <button
                            onClick={() => setSelectedNotice(null)}
                            className="absolute top-5 right-5 bg-yellow-50 rounded-full w-11 h-11 shadow-xl text-xl font-bold hover:bg-primary hover:text-white"
                        >
                            &times;
                        </button>
                        <motion.div
                            initial={{ opacity: 0, y: 60, scale: 0.9, rotate: -3 }}
                            animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
                            exit={{ opacity: 0, y: 40, scale: 0.9 }}
                            transition={{ duration: 0.4, ease: "easeOut" }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative w-full max-w-xl max-h-[90vh] bg-yellow-50 rounded-3xl shadow-2xl p-8  overflow-y-auto"
                        >
                            {selectedNotice?.payload?.title && <h3 className="text-2xl font-bold text-primary text-center mb-2 tracking-wide capitalize">
                                {selectedNotice?.payload?.title}
                            </h3>}
                            <div className="w-16 h-1 bg-orange-300 mx-auto rounded-full mb-6" />

                            {selectedNotice?.payload?.date && (
                                <p className="text-center text-sm text-primary mb-4">
                                    {t(I18N_KEYS.DATE)} : {formatDate(selectedNotice.payload?.date)}
                                </p>
                            )}

                            {selectedNotice?.payload?.message &&
                                <p className=" mb-4">
                                    {selectedNotice.payload?.message}
                                </p>
                            }

                            {selectedNotice?.payload?.description && <p
                                dangerouslySetInnerHTML={{ __html: selectedNotice?.payload?.description }}
                            ></p>}
                        </motion.div>
                    </motion.div>
                </AnimatePresence>
            )}
        </>
    );
};

export default NoticeBoard;


